/* lp-teardown.js — the bindery teardown: an Inspire notebook drawn as five plates that separate as
   you scroll, one layer's copy at a time in the lens beside it. On phones the plates arrive already
   apart and taps drive the copy.

   PORTED 9 Sep 2026 from the prototype (website/Leatherpress_Website_v4_branch2.html — the bindery
   IIFE's BINDSTAGE==='exploded' branch: IPTS, LAYERS, paint(), hold(), tick()). Behaviour identical.
   What changed shape, and why:

   - IIFE → custom element <lp-teardown>. connectedCallback builds and binds, disconnectedCallback
     unbinds. The theme editor re-mounts a section when it is edited, so the element re-initialises
     by construction — no shopify:section:load bookkeeping and no double binding (brief §5).
   - The five layers' copy (the old IPTS array) is the section's blocks, handed over as JSON in a
     <script type="application/json"> inside the element. The plate ART stays code: it is drawn, not
     written, and it is the one part a design team should not be editing.
   - The CURPAGE check is gone: this element only exists on the page it belongs to.
   - SCROLL SOURCE. Horizon scrolls .page-wrapper, not the window, at ≥990px (base.css). Progress is
     measured with getBoundingClientRect, which is viewport-relative and so indifferent to which
     element scrolls — only the EVENT has to be heard. The listener therefore sits on the window AND
     on .page-wrapper when one exists. Simpler than tracking Horizon's media query, and this file
     stays free of Horizon internals. */
(function () {
  'use strict';
  if (!('customElements' in window) || customElements.get('lp-teardown')) return;

  /* An Inspire taken apart, drawn rather than photographed. The plates deliberately do NOT reuse the
     heirloom set: there is no ribbed spine, no gilding and no slipcase on a companion, and drawing
     them would have been a nicer picture of the wrong product. Pacific blue because it is the
     colorway the pebbling reads best on. Order is outside-in, and it is fixed: the fifth plate is
     the accordion pocket, the one thing in the range no other collection has. */
  var LAYERS = [
    /* THE ELASTIC LEADS. It used to sit second, under the cover plate, where the band was almost
       invisible — and the band is the point of that layer. It is also where the band belongs
       physically: round the OUTSIDE of the cover. Mostly transparent so it reads as a band on a
       plane rather than a second slab of leather. One band, unbroken — a pen drawn under it read as
       a white block across the line at this scale. Blue rather than near-black so it reads as part
       of the same object as the cover beneath it. */
    '<div class="bplate" style="background:rgba(63,93,162,.10);outline:1px solid rgba(63,93,162,.28);outline-offset:-1px;">'
      + '<div style="position:absolute;top:-10px;bottom:-10px;right:34px;width:9px;background:linear-gradient(180deg,#4a6cb5,#2f4a7d);box-shadow:0 0 14px rgba(38,64,110,.5);"></div></div>',
    /* the cover: pebbled leather over a board */
    '<div class="bplate" style="background:linear-gradient(135deg,#2f4f86,#3f5da2 55%,#26406e);box-shadow:inset 0 0 40px rgba(0,0,0,.3);">'
      + '<div class="bplate" style="opacity:.5;mix-blend-mode:overlay;background-image:radial-gradient(rgba(255,255,255,.55) .7px,transparent .8px);background-size:5px 5px;"></div>'
      + '<span style="position:absolute;right:14px;bottom:12px;width:12px;height:12px;border:1px solid rgba(255,255,255,.35);"></span></div>',
    /* the block: ruled pages, with the ribbon marker sewn in at the head and running past the tail —
       matched to the cover, which is why it is drawn in the same blue as the boards */
    (function () {
      var s = '<div class="bplate" style="background:#fdfbf7;box-shadow:inset 0 0 18px rgba(25,21,16,.06);">';
      s += '<div style="position:absolute;inset:22px 18px;background-image:repeating-linear-gradient(to bottom,transparent 0,transparent 8px,rgba(25,21,16,.16) 8px,rgba(25,21,16,.16) 9px);"></div>';
      s += '<div style="position:absolute;left:0;top:0;bottom:0;width:5px;background:rgba(25,21,16,.1);"></div>';
      s += '<div style="position:absolute;left:26%;top:-4px;width:9px;height:118%;background:linear-gradient(180deg,#3f5da2,#2f4a7d);opacity:.92;box-shadow:0 2px 6px rgba(0,0,0,.25);"></div>';
      s += '<div style="position:absolute;left:26%;bottom:-16px;width:9px;height:16px;background:#2f4a7d;clip-path:polygon(0 0,100% 0,100% 60%,50% 100%,0 60%);"></div>';
      return s + '</div>';
    })(),
    /* the thread: six stitches through the fold */
    (function () {
      var s = '<div class="bplate" style="background:#f7f3ec;"><svg viewBox="0 0 100 130" width="100%" height="100%" preserveAspectRatio="none" aria-hidden="true">'
        + '<path d="M50 6 L50 124" stroke="rgba(25,21,16,.18)" stroke-width="1"/>';
      for (var i = 0; i < 6; i++) s += '<path d="M44 ' + (12 + i * 20) + ' C 58 ' + (16 + i * 20) + ', 42 ' + (24 + i * 20) + ', 56 ' + (28 + i * 20) + '" fill="none" stroke="#a8875a" stroke-width="1.4"/>';
      return s + '</svg></div>';
    })(),
    /* the pocket: a folded flap across the lower half of the back board, a ticket standing out of it */
    '<div class="bplate" style="background:linear-gradient(135deg,#26406e,#1d3358);box-shadow:inset 0 0 30px rgba(0,0,0,.28);">'
      + '<div style="position:absolute;left:14px;right:14px;bottom:16px;height:44%;background:linear-gradient(180deg,#31518b,#22395f);border-top:1px solid rgba(255,255,255,.18);"></div>'
      + '<div style="position:absolute;left:14px;right:14px;bottom:16px;height:44%;background-image:repeating-linear-gradient(90deg,rgba(0,0,0,.16) 0,rgba(0,0,0,.16) 1px,transparent 1px,transparent 13px);"></div>'
      + '<div style="position:absolute;left:34%;bottom:calc(16px + 34%);width:44px;height:26px;background:#e8dcc4;transform:rotate(-7deg);box-shadow:0 3px 8px rgba(0,0,0,.35);"></div>'
      + '<div style="position:absolute;left:36%;bottom:calc(16px + 38%);width:34px;height:2px;background:rgba(25,21,16,.35);transform:rotate(-7deg);"></div></div>'
  ];

  function clamp01(v) { return Math.min(1, Math.max(0, v)); }

  class LPTeardown extends HTMLElement {
    connectedCallback() {
      var self = this;
      /* idempotent: reordering in the editor reconnects the SAME node, so unbind before rebinding */
      this.disconnectedCallback();

      var stage = this.querySelector('[data-stage]');
      var host = this.querySelector('[data-stagewrap]');
      var ticks = this.querySelector('[data-ticks]');
      var lens = this.querySelector('[data-lens]');
      var data = this.querySelector('script[type="application/json"][data-layers]');
      if (!stage || !host || !ticks || !lens || !data) return;

      var IPTS;
      try { IPTS = JSON.parse(data.textContent) || []; } catch (e) { IPTS = []; }
      var n = Math.min(LAYERS.length, IPTS.length);
      if (!n) return;
      /* (i - mid): 2 for the full set of five, exactly the prototype's (i-2); still centred if a
         block is ever removed */
      var mid = (n - 1) / 2;

      var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      var iactive = -1, locked = null, lockTimer = null, spread = 0, step = 0, frame = 0;

      function field(k) { return lens.querySelector('[data-lens-' + k + ']'); }
      var img = field('img');
      function fill(x) {
        if (img) {
          /* alt travels with the photograph (11 Sep 2026). The section passes through whatever alt
             text the merchant set on the image in Content > Files; swapping src without swapping alt
             would leave every layer describing the first layer's picture. */
          if (x.img) { img.src = x.img; img.alt = x.alt || ''; img.hidden = false; }
          else { img.removeAttribute('src'); img.alt = ''; img.hidden = true; }
        }
        ['num', 'h', 'p', 's'].forEach(function (k) { var e = field(k); if (e) e.textContent = x[k] || ''; });
      }
      function setLayer(i) {
        if (i === iactive) return;
        iactive = i;
        fill(IPTS[i]);
        Array.prototype.forEach.call(ticks.children, function (t, k) { t.classList.toggle('on', k === i); });
      }
      /* holding a layer stops the scroll from stealing it back mid-read — for nine seconds */
      function hold(i) {
        locked = i; setLayer(i); paint();
        clearTimeout(lockTimer);
        lockTimer = setTimeout(function () { locked = null; paint(); }, 9000);
        /* on a phone the copy sits below the stage (the 25 Aug order flip), so a tap on a plate walks
           the panel into view. block:'nearest' keeps it gentle — no scroll if it is already visible. */
        /* (the phone used to scroll the copy into view here; the copy is pinned now) */
      }

      /* build: plates, then the flat label overlay, then the ticks */
      stage.innerHTML = '';
      ticks.innerHTML = '';
      var layerEls = [], tagEls = [];
      for (var i = 0; i < n; i++) {
        (function (i) {
          var b = document.createElement('button');
          b.className = 'blayer';
          b.type = 'button';
          b.setAttribute('aria-label', IPTS[i].num || ('layer ' + (i + 1)));
          b.innerHTML = '<div class="bbody">' + LAYERS[i] + '</div>';
          b.addEventListener('click', function () { hold(i); });
          stage.appendChild(b);
          layerEls.push(b);
        })(i);
      }
      /* the labels live in a flat overlay OUTSIDE the perspective, tracking each layer's vertical
         position — inside the plates they were rotated flat and covered by the sheets above */
      var tagbox = document.createElement('div');
      tagbox.className = 'btags';
      for (var j = 0; j < n; j++) {
        var s = document.createElement('span');
        s.className = 'btagx'; s.textContent = IPTS[j].tag || '';
        tagbox.appendChild(s); tagEls.push(s);
      }
      stage.appendChild(tagbox);
      for (var k = 0; k < n; k++) {
        (function (k) {
          var t = document.createElement('button');
          t.type = 'button';
          t.setAttribute('aria-label', IPTS[k].num || ('layer ' + (k + 1)));
          t.addEventListener('click', function () { hold(k); });
          ticks.appendChild(t);
        })(k);
      }

      function paint() {
        var narrow = window.innerWidth < 900;
        var idx = locked === null ? Math.min(n - 1, Math.floor(step * n)) : locked;
        layerEls.forEach(function (el, i) {
          var lift = spread * (i - mid) * (narrow ? 46 : 74);
          el.style.transform = 'translateY(calc(-50% + ' + lift + 'px)) rotateX(58deg) rotateZ(-32deg) translateZ(' + (i === idx ? 26 : 0) + 'px)';
          el.style.zIndex = String(layerEls.length - i);
          el.classList.toggle('on', i === idx);
          var tg = tagEls[i];
          if (tg) { tg.style.transform = 'translateY(calc(-50% + ' + lift + 'px))'; tg.classList.toggle('on', i === idx); }
        });
        setLayer(idx);
      }
      /* TWO SEPARATE THINGS, which is the fix for the unevenness. 'spread' is how far apart the
         plates are — eased, with a short settle at each end. 'step' is WHICH layer is active, and it
         runs evenly across the whole scroll instead of being derived from spread (spread saturates
         early, so the last layer used to get twice the scroll of the middle three). */
      function tick() {
        frame = 0;
        if (reduced) { spread = 1; step = 0; paint(); return; }
        var r = host.getBoundingClientRect();
        /* 23 Sep 2026: phones use the same maths as desktop — the section's CSS now pins stage and copy
           inside a tall runway below 900px too, so progress through the host drives both. */
        var total = r.height - window.innerHeight;
        var prog = total > 0 ? clamp01(-r.top / total) : 0;
        spread = clamp01((prog - 0.04) / 0.70);
        step = clamp01(prog);
        paint();
      }
      function onScroll() { if (!frame) frame = requestAnimationFrame(tick); }
      /* the theme editor: selecting a layer block holds that layer, so the design team sees the copy
         they are editing */
      function onBlockSelect(e) {
        var t = e.target;
        if (!t || !self.contains(t) || !t.closest) return;
        var src = t.closest('[data-layer-index]');
        if (!src) return;
        var i = parseInt(src.getAttribute('data-layer-index'), 10);
        if (i >= 0 && i < n) hold(i);
      }

      var wrapper = document.querySelector('.page-wrapper');
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      if (wrapper) wrapper.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('shopify:block:select', onBlockSelect);

      this._unbind = function () {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        if (wrapper) wrapper.removeEventListener('scroll', onScroll);
        document.removeEventListener('shopify:block:select', onBlockSelect);
        if (frame) cancelAnimationFrame(frame);
        clearTimeout(lockTimer);
      };
      this.hold = hold;

      spread = reduced ? 1 : 0;
      paint();
      onScroll();
    }

    disconnectedCallback() {
      if (this._unbind) { this._unbind(); this._unbind = null; }
    }
  }

  customElements.define('lp-teardown', LPTeardown);
})();
